import React, { useState, useEffect, useContext, useCallback } from "react";
import ChangingDocument from "./ChangingDocument/ChangingDocument";
import { loadEmployeeChangingDocuments } from "../../../services/employees/employee-documents-api.service";
import { PageByDocType } from "../../../models/employee-documents";
import { State } from "./interfaces";
import { EmployeeDataContext } from "../EmployeePersonalAndChangingDocuments";
import { DOCUMENT_TYPES } from "../../../utils/constants/constants";
import useWindowSize from "../../../custom-hooks/useWindowSize/useWindowSize";

const PAGE_SIZE = 5;
interface Props {
  updateParentLoading: (isLoading: boolean) => void;
  shouldReload?: boolean;
}

const EmployeeChangingDocuments = ({ updateParentLoading, shouldReload }: Props) => {
  const { employee, forManager } = useContext(EmployeeDataContext);
  const { width } = useWindowSize();
  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    changingDocuments: [],
    pagingByDocType: [],
    changingDocumentRawRequests: [],
    changingDocumentRequests: [],
    pageByDocSize: 5,
  });

  const getEmployeeChangingDocuments = useCallback(
    async (employeeId: string, signal?: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));
      updateParentLoading(true);
      try {
        const { items, error } = await loadEmployeeChangingDocuments(
          employeeId,
          state.pagingByDocType,
          forManager,
          0,
          100,
          state.pageByDocSize,
          signal
        );

        if (!error) {
          setState((state) => ({
            ...state,
            loading: false,
            error: "",
            changingDocuments: !!state.pagingByDocType?.length
              ? items.map((item) => ({
                  ...item,
                  page: state.pagingByDocType?.find((i) => i.documentTypeId === item.documentType?.id)?.page ?? {
                    pageNumber: 0,
                    pageSize: state.pageByDocSize,
                  },
                }))
              : items.map((item) => ({
                  ...item,
                  page: { pageNumber: 0, pageSize: state.pageByDocSize },
                })),
          }));
        } else {
          setState((state) => ({ ...state, error }));
        }
      } catch (error) {
        setState((state) => ({ ...state, error }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
        updateParentLoading(false);
      }
    },
    [state.pageByDocSize, state.pagingByDocType]
  );

  const handleChangePageNumber = async (docTypeId: string | undefined, pageNumber: number) => {
    const computedChangingDocument = state.changingDocuments.find(({ documentType }) => documentType?.id === docTypeId);

    const pagingByDocType = state.changingDocuments.map(({ documentType, page }) => ({
      documentTypeId: documentType?.id ?? "",
      page: {
        ...page,
        ...(documentType?.id === computedChangingDocument?.documentType?.id ? { pageNumber } : {}),
      },
    }));

    setState((state) => ({ ...state, pagingByDocType }));
  };

  useEffect(() => {
    const abortController = new AbortController();
    if (employee?.id) {
      getEmployeeChangingDocuments(employee.id, abortController.signal);
    }
    return () => abortController.abort();
  }, [employee?.id, state.pagingByDocType, state.pageByDocSize]);

  useEffect(() => {
    const abortController = new AbortController();
    if (shouldReload && employee?.id) getEmployeeChangingDocuments(employee.id, abortController.signal);

    return () => abortController.abort();
  }, [shouldReload, employee?.id]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      pagingByDocType: [],
      pageByDocSize: width < 720 ? 5 : width < 1110 ? 10 : 20,
    }));
  }, [width]);

  return (
    <>
      {state.changingDocuments.map((changingDocument, index) => (
        <ChangingDocument
          key={index}
          changingDocument={changingDocument}
          handleChangePageNumber={handleChangePageNumber}
          handleNotify={() => {
            employee && getEmployeeChangingDocuments(employee?.id);
          }}
          pageByDocTypeSize={state.pageByDocSize}
        />
      ))}
    </>
  );
};

export default EmployeeChangingDocuments;
